import { type FC, useState } from 'react';

import CustomTable from 'components/table/CustomTable';
import { TagComponent } from 'components/tag/TagComponent';
import { SubTitle } from 'components/text/SubTitle';
import { Tooltip, Typography, formatDate, openModal } from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { FunctionalTestMethod } from 'modules/dashboard/core/enum';
import { OveralExercise } from 'modules/dashboard/styles/lesson';

import { ReactComponent as ViewNoteIcon } from 'assets/icons/note-32.svg';

import type { LearnerResult } from '../core/type';
import type { HeaderTableItem } from 'components/table/type';

import { convertToTimeString } from 'helpers';
import { includes, isNull, isUndefined, orderBy, reduce, sumBy } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { MAX_ADDITIONAL_SCORE } from '../../../constants';
import { ExerciseContentType, LearnerResultStatus, LessonAssessmentType } from '../core/enum';
import { LearnerResultStatusCard } from './LearnerResultStatusCard';
import { ViewNoteModal } from './ViewNoteModal';

export const LearnResult: FC<{
  type: LessonAssessmentType;
  learnerResults: LearnerResult[];
  completedOn?: string;
  lessonType?: string;
}> = ({ type, learnerResults, completedOn, lessonType }) => {
  const [comment, setComment] = useState<string>('');

  const header: HeaderTableItem[] = [
    {
      title: '',
      field: 'collapse',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
    },
    {
      title: 'Client',
      field: 'learner',
      style: {
        width: '65%',
        minWidth: 'calc(65% - 550px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Status',
      field: 'result',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Completed on',
      field: 'isCompleted',
      style: {
        width: '15%',
        minWidth: 200,
      },
    },
  ];

  const headersChildrenLesson: HeaderTableItem[] = [
    {
      title: '#',
      field: 'index',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        textAlign: 'center',
      },
    },
    {
      title: 'Exercise',
      field: 'exercise',
      style: {
        width: '65%',
        minWidth: 'calc(65% - 550px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Set',
      field: 'set',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Reps',
      field: 'reps',
      style: {
        width: '15%',
        minWidth: 200,
      },
    },
    {
      title: '',
      field: 'viewNote',
      style: {
        width: 60,
        minWidth: 20,
      },
    },
  ];

  const headersChildrenAssessment: HeaderTableItem[] = [
    {
      title: '#',
      field: 'index',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        textAlign: 'center',
      },
    },
    {
      title: 'Functional test',
      field: 'functionalTest',
      style: {
        width: '60%',
        minWidth: 'calc(60% - 600px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Metric',
      field: 'metric',
      style: {
        width: '30%%',
        minWidth: 400,
      },
    },
    {
      title: '',
      field: 'viewNote',
      style: {
        width: 60,
        minWidth: 20,
      },
    },
  ];

  const renderBody = (index: number, row: any, column: HeaderTableItem) => {
    switch (column.field) {
      case 'learner':
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              justifyContent: 'space-between',
            }}
          >
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              {row.learner?.name}
            </Typography>
            {Boolean(
              !row.learner?.levelCFS &&
                includes([ExerciseContentType.DIY, ExerciseContentType.SUPERVISED], lessonType),
            ) && <TagComponent color={COLOR_CONFIG.red} text={'No BFT'} />}
          </div>
        );

      case 'score':
        if (
          !row.learner?.levelCFS &&
          includes([ExerciseContentType.DIY, ExerciseContentType.SUPERVISED], lessonType)
        ) {
          return <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>-</Typography>;
        }

        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.learner.score ?? 0}/{row.learner.maxScore}
          </Typography>
        );

      case 'result':
        return <LearnerResultStatusCard status={row.learner.status} />;

      case 'isCompleted':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.learner.completedOn
              ? formatDate(row.learner.completedOn, 'iii, d LLL, hh:mm a ')
              : '-'}
          </Typography>
        );
    }
  };

  const renderBodyChildren = (index: number, row: any, column: HeaderTableItem) => {
    switch (column.field) {
      case 'index':
        return <Typography>{index + 1}</Typography>;

      case 'exercise':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={row.exercise.thumbnail} style={{ width: 48, height: 48, borderRadius: 12 }} />
            <div>
              <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                {row.exercise.name}
              </Typography>
              <Typography style={{ fontSize: '16px', lineHeight: '22px', color: '#808080' }}>
                {row.exercise.category.name}
              </Typography>
            </div>
          </div>
        );

      case 'score':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.detail ? row.detail.score : 0}/{Number(row.reps) * Number(row.sets)}
          </Typography>
        );

      case 'set':
        return (
          <>
            {row.detail &&
              orderBy(row.detail.userClassScheduleExerciseSets, 'sets').map((el: any) => {
                return (
                  <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                    {el.sets}
                  </Typography>
                );
              })}
          </>
        );

      case 'reps':
        return (
          <>
            {row.detail &&
              orderBy(row.detail.userClassScheduleExerciseSets, 'sets').map((el: any) => {
                return (
                  <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                    {el.score}/{el.reps}
                  </Typography>
                );
              })}
          </>
        );

      case 'viewNote':
        return row?.detail?.comment ? (
          <>
            <Tooltip placement="top" arrow={false} content="View note" offset={[0, -4]}>
              <ViewNoteIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setComment(row.detail.comment);
                  openModal(MODAL_KEY.VIEW_NOTE);
                }}
              />
            </Tooltip>
          </>
        ) : null;
    }
  };

  const renderBodyChildrenAssessment = (index: number, row: any, column: HeaderTableItem) => {
    const maxScore = row.functionalTest.maxScore;
    const totalMaxScore = row.functionalTest.isAddition
      ? maxScore + MAX_ADDITIONAL_SCORE
      : maxScore;

    const score =
      Number(row.detail?.score) > Number(row.functionalTest.maxScore)
        ? Number(row.functionalTest.maxScore)
        : Number(row.detail?.score);
    const additionalScore =
      row.functionalTest.isAddition && row.detail?.scoreAddition ? row.detail?.scoreAddition : 0;
    const totalScore = score + Math.min(additionalScore, MAX_ADDITIONAL_SCORE);

    switch (column.field) {
      case 'index':
        return (
          <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
            {index + 1}.
          </Typography>
        );

      case 'functionalTest':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src={row.functionalTest.thumbnail}
              style={{ width: 48, height: 48, borderRadius: 12 }}
            />
            <div>
              <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
                {row.functionalTest.name}
              </Typography>
              {row.functionalTest.type ? (
                <SubTitle leftTitle={'Score entry'} rightTitle={row.functionalTest.type} />
              ) : (
                <Typography variant="body-3" color={COLOR_CONFIG['neutral-text']}>
                  {row.functionalTest.method?.toLowerCase() === FunctionalTestMethod.Kg
                    ? 'Weight entry'
                    : row.functionalTest.method?.toLowerCase() === FunctionalTestMethod.Time
                      ? 'Time entry'
                      : 'Rep count entry'}
                </Typography>
              )}
            </div>
          </div>
        );

      case 'score':
        return (
          <>
            {row.functionalTest.method.toLowerCase() !== FunctionalTestMethod.Kg &&
              row.functionalTest.method.toLowerCase() !== FunctionalTestMethod.Time && (
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  {row?.detail ? `${totalScore}/${totalMaxScore}` : 'No score'}
                </Typography>
              )}
          </>
        );

      case 'metric':
        if (!row?.detail) {
          return <></>;
        }

        if (row.functionalTest.method.toLowerCase() === 'enter score') {
          return (
            <>
              {row.functionalTest.isAddition &&
                !isUndefined(row.detail.scoreAddition) &&
                !isNull(row.detail.scoreAddition) && (
                  <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                    {` Reps in 30s: ${row.detail.scoreAddition}`} <br />
                  </Typography>
                )}
              <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                {` ${row.functionalTest.isAddition ? 'Time for 5 reps' : 'Time'}: ${convertToTimeString(row.detail.timeCount, true)}`}{' '}
                <br />
              </Typography>
            </>
          );
        }

        if (row.functionalTest.method.toLowerCase() === FunctionalTestMethod.Kg) {
          return (
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              Weight: {row.detail.score ?? 0} kg
            </Typography>
          );
        }

        if (row.functionalTest.method.toLowerCase() === FunctionalTestMethod.Time) {
          return (
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              Time: {convertToTimeString(Number(row.detail.timeCount ?? 0), true)}
            </Typography>
          );
        }

        return (
          <>
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              Reps: {row.detail.score ?? 0} <br />
              Time: {convertToTimeString(row.detail.timeCount, true)} <br />
            </Typography>
            {/* {row.functionalTest.isSO2AndHR && (
              <div>
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  Pre-test:{' '}
                  {!row.detail.preHR && !row.detail.preSpO2 ? (
                    <span style={{ color: '#808080' }}>No data</span>
                  ) : (
                    `${row.detail.preSpO2 ? `SpO2 = ${row.detail.preSpO2}%,` : ''} ${row.detail.preHR ? `HR = ${row.detail.preHR} bpm` : ''}`
                  )}
                </Typography>
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  Post-test:{' '}
                  {!row.detail.postHR && !row.detail.postSpO2 ? (
                    <span style={{ color: '#808080' }}>No data</span>
                  ) : (
                    `${row.detail.postSpO2 ? `SpO2 = ${row.detail.postSpO2}%,` : ''} ${row.detail.postHR ? `HR = ${row.detail.postHR} bpm` : ''}`
                  )}
                </Typography>
                <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                  Recovery:{' '}
                  {!row.detail.recoveryHR && !row.detail.recoverySpO2 ? (
                    <span style={{ color: '#808080' }}>No data</span>
                  ) : (
                    `${row.detail.recoverySpO2 ? `SpO2 = ${row.detail.recoverySpO2}%,` : ''} ${row.detail.recoveryHR ? `HR = ${row.detail.recoveryHR} bpm` : ''}`
                  )}
                </Typography>
              </div>
            )} */}
          </>
        );

      case 'viewNote':
        return row?.detail?.comment ? (
          <>
            <Tooltip placement="top" arrow={false} content="View note" offset={[0, -4]}>
              <ViewNoteIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setComment(row.detail.comment);
                  openModal(MODAL_KEY.VIEW_NOTE);
                }}
              />
            </Tooltip>
          </>
        ) : null;
    }
  };

  const bodies = learnerResults?.map((el) => ({
    ...el,
    isHadCFS: Boolean(el.learner.levelCFS),
    learner: {
      ...el.learner,
      score:
        type === LessonAssessmentType.LESSONS
          ? sumBy(el.items, 'detail.score')
          : sumBy(
              el.items
                .filter(
                  (item) =>
                    String(item?.functionalTest?.method.toLowerCase()) !==
                      FunctionalTestMethod.Kg &&
                    String(item?.functionalTest?.method.toLowerCase()) !==
                      FunctionalTestMethod.Time,
                )
                .map((it: any) => {
                  const score =
                    Number(it?.detail?.score) > Number(it?.functionalTest?.maxScore)
                      ? Number(it?.functionalTest?.maxScore ?? 0)
                      : Number(it?.detail?.score ?? 0);
                  const additionalScore =
                    it.functionalTest?.isAddition && it.detail?.scoreAddition
                      ? it.detail?.scoreAddition
                      : 0;
                  const totalScore = score + Math.min(additionalScore, MAX_ADDITIONAL_SCORE);

                  return {
                    ...it,
                    score: totalScore,
                  };
                }),
              'score',
            ),
      maxScore:
        type === LessonAssessmentType.LESSONS
          ? sumBy(el.items, 'maxScore')
          : sumBy(
              el.items
                .filter(
                  (item) =>
                    String(item?.functionalTest?.method.toLowerCase()) !==
                      FunctionalTestMethod.Kg &&
                    String(item?.functionalTest?.method.toLowerCase()) !==
                      FunctionalTestMethod.Time,
                )
                .map((it: any) => {
                  const maxScore = it.functionalTest?.maxScore || 0;
                  const additionalScore = it.functionalTest?.isAddition ? MAX_ADDITIONAL_SCORE : 0;
                  const totalMaxScore = maxScore + additionalScore;

                  return {
                    ...it,
                    maxScore: totalMaxScore,
                  };
                }),
              'maxScore',
            ),
      isCompleted: el.items.find((item: any) => item.detail) && completedOn ? true : false,
    },
    id: el.learner.id,
  }));

  const totalCompleted = reduce(
    learnerResults,
    (count, el) => (el.learner.status === LearnerResultStatus.Completed ? count + 1 : count),
    0,
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <OveralExercise>
          <Typography>
            {totalCompleted}/{learnerResults.length} completed
          </Typography>
        </OveralExercise>
      </div>

      <CustomTable
        headers={header}
        bodies={bodies}
        renderBody={renderBody}
        headersChildren={
          type === LessonAssessmentType.LESSONS ? headersChildrenLesson : headersChildrenAssessment
        }
        renderBodyChildren={
          type === LessonAssessmentType.LESSONS ? renderBodyChildren : renderBodyChildrenAssessment
        }
        styleTable={{ marginTop: 0 }}
        noDataText={
          includes([ExerciseContentType.DIY, ExerciseContentType.SUPERVISED], lessonType)
            ? 'This client has no BFT'
            : ''
        }
        emptyResultMsg="Event is not scheduled. No client data"
      />

      <ViewNoteModal comment={comment} onCancel={() => setComment('')} />
    </div>
  );
};
